// ── Mapeamento de importação de leads via CSV ─────────────────────
// Converte as linhas de uma planilha (parseCsv) em linhas prontas para a RPC
// `criar_lead_externo` (upsert idempotente por email/telefone). Não conhece
// Supabase nem workspace/responsável — isso é resolvido no fluxo (LeadFlowService
// .importLeads) a partir do contexto do usuário que importa.
//
// Dois perfis de colunas:
//   • enterprise — casa 1:1 com o modal B2B (Empresa/Pessoa, funcionários,
//     nicho, localização, faturamento, etiqueta). Reaproveita colunas do banco
//     com significado B2B, IGUAL ao LeadModal enterprise:
//        quantidade_funcionarios → renda_liquida   nicho → area_trabalho
//        localizacao → sub_origem                  faturamento_empresa → patrimonio
//        etiqueta_card → canal                     nome_contato → email_secundario
//   • standard — modelo completo com dados de marketing (origem, campanha,
//     criativo, UTMs, funil/etapa).
//
// Em ambos os perfis o responsável NÃO vem do CSV: quem importa vira o
// responsável (regra do fluxo de criação).

import type { ParsedCsv } from '@utils';

export type ImportProfile = 'enterprise' | 'techfinance' | 'standard' | 'simples';

// Valores iguais para a lista inteira, escolhidos no modal em vez de virarem
// coluna. Repetir "origem" em 500 linhas não informa nada e basta um erro de
// digitação numa delas para o lead sumir do filtro depois.
export interface ImportDefaults {
  fields?: Partial<ImportLeadFields>;
  tagNames?: string[];
}

// Campos do lead já com os nomes do banco (subset de CriarLeadParams, sem
// workspace/responsável). `nome` é o único obrigatório.
export interface ImportLeadFields {
  nome: string;
  email?: string;
  telefone?: string;
  email_secundario?: string;
  cargo?: string;
  area_trabalho?: string;
  renda_liquida?: string;
  valor_investido?: string;
  patrimonio?: string;
  origem?: string;
  sub_origem?: string;
  canal?: string;
  campanha?: string;
  publico?: string;
  criativo?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  observacoes?: string;
  status?: string;
  funnel_nome?: string;
  stage_nome?: string;
}

export interface ImportLeadRow {
  rowNumber: number;   // linha na planilha (2 = primeira linha de dados)
  label: string;       // nome/empresa para exibição no resultado
  fields: ImportLeadFields;
  tagNames: string[];  // aplicadas por NOME via RPC (modo 'add')
}

export interface ImportRowError {
  rowNumber: number;
  message: string;
}

export interface MapImportResult {
  profile: ImportProfile;
  rows: ImportLeadRow[];          // linhas válidas, prontas para importar
  errors: ImportRowError[];       // linhas descartadas (com motivo)
  mappedColumns: string[];        // cabeçalhos reconhecidos
  ignoredColumns: string[];       // cabeçalhos ignorados (não fazem parte do modelo)
  hasRequiredColumn: boolean;     // a coluna-chave (nome/empresa) existe?
}

// Cabeçalho do CSV → chave de ImportLeadFields. `tags` é tratado à parte.
type ColumnMap = Partial<Record<string, keyof ImportLeadFields>>;

const ENTERPRISE_MAP: ColumnMap = {
  empresa_ou_pessoa: 'nome',
  nome_contato: 'email_secundario',
  email: 'email',
  telefone: 'telefone',
  cargo: 'cargo',
  quantidade_funcionarios: 'renda_liquida',
  nicho: 'area_trabalho',
  localizacao: 'sub_origem',
  faturamento_empresa: 'patrimonio',
  etiqueta_card: 'canal',
  observacoes: 'observacoes',
};

const STANDARD_MAP: ColumnMap = {
  nome: 'nome',
  email: 'email',
  telefone: 'telefone',
  email_secundario: 'email_secundario',
  cargo: 'cargo',
  area_trabalho: 'area_trabalho',
  renda_liquida: 'renda_liquida',
  valor_investido: 'valor_investido',
  patrimonio: 'patrimonio',
  origem: 'origem',
  sub_origem: 'sub_origem',
  canal: 'canal',
  campanha: 'campanha',
  publico: 'publico',
  criativo: 'criativo',
  utm_source: 'utm_source',
  utm_medium: 'utm_medium',
  utm_campaign: 'utm_campaign',
  utm_content: 'utm_content',
  observacoes: 'observacoes',
  status: 'status',
  funnel_nome: 'funnel_nome',
  stage_nome: 'stage_nome',
};

// TechFinance — igual ao padrão em marketing/origem/canal, mas com cabeçalhos
// próprios para os 4 campos de qualificação reaproveitados (texto livre).
//   qualificacao_lead → area_trabalho   pontuacao     → renda_liquida
//   ja_conhece_ed     → cargo           busca_formacao → patrimonio
const TECHFINANCE_MAP: ColumnMap = {
  nome: 'nome',
  email: 'email',
  telefone: 'telefone',
  qualificacao_lead: 'area_trabalho',
  pontuacao: 'renda_liquida',
  ja_conhece_ed: 'cargo',
  busca_formacao: 'patrimonio',
  origem: 'origem',
  canal: 'canal',
  campanha: 'campanha',
  publico: 'publico',
  criativo: 'criativo',
  utm_source: 'utm_source',
  utm_medium: 'utm_medium',
  utm_campaign: 'utm_campaign',
  utm_content: 'utm_content',
  observacoes: 'observacoes',
  status: 'status',
  funnel_nome: 'funnel_nome',
  stage_nome: 'stage_nome',
};

// Lista simples — o CSV traz só o que muda de contato para contato. Origem,
// canal, campanha e tags valem para a lista toda e vêm do modal (ImportDefaults).
// Pensado para montar um disparo: o mínimo para o lead existir e ter telefone.
const SIMPLE_MAP: ColumnMap = {
  nome: 'nome',
  email: 'email',
  telefone: 'telefone',
  observacoes: 'observacoes',
};

// Colunas do modelo simples, sem o `tags` que o templateHeadersFor acrescenta:
// no perfil simples a tag vem do modal, a coluna é só uma cortesia.
export const SIMPLE_HEADERS = Object.keys(SIMPLE_MAP);

const mapForProfile = (profile: ImportProfile): ColumnMap =>
  profile === 'enterprise' ? ENTERPRISE_MAP
    : profile === 'techfinance' ? TECHFINANCE_MAP
      : profile === 'simples' ? SIMPLE_MAP
        : STANDARD_MAP;

// Cabeçalho da coluna-chave (nome do lead) por perfil.
export const requiredHeaderFor = (profile: ImportProfile): string =>
  profile === 'enterprise' ? 'empresa_ou_pessoa' : 'nome';

// Colunas esperadas por perfil (usado para gerar o modelo/legenda na UI).
export const templateHeadersFor = (profile: ImportProfile): string[] =>
  [...Object.keys(mapForProfile(profile)), 'tags'];

export const mapImportRows = (parsed: ParsedCsv, profile: ImportProfile, defaults: ImportDefaults = {}): MapImportResult => {
  const map = mapForProfile(profile);
  const requiredHeader = requiredHeaderFor(profile);
  const known = new Set([...Object.keys(map), 'tags']);

  // `nome` fora: é sempre da linha. Vazio fora: default em branco não deve
  // apagar o que a coluna trouxe.
  const defaultFields = Object.fromEntries(
    Object.entries(defaults.fields ?? {}).filter(([key, value]) => key !== 'nome' && String(value ?? '').trim()),
  ) as Partial<ImportLeadFields>;
  const defaultTags = (defaults.tagNames ?? []).map(tag => tag.trim()).filter(Boolean);

  const mappedColumns = parsed.headers.filter(h => known.has(h));
  const ignoredColumns = parsed.headers.filter(h => h && !known.has(h));
  const hasRequiredColumn = parsed.headers.includes(requiredHeader);

  const rows: ImportLeadRow[] = [];
  const errors: ImportRowError[] = [];

  parsed.rows.forEach((raw, i) => {
    const rowNumber = i + 2; // +1 cabeçalho, +1 base-1
    const nome = (raw[requiredHeader] ?? '').trim();
    if (!nome) {
      errors.push({
        rowNumber,
        message: `Linha ${rowNumber}: "${requiredHeader}" (obrigatório) vazio — ignorada.`,
      });
      return;
    }

    // Default primeiro, coluna depois: se a planilha trouxer o campo, ela ganha.
    const fields: ImportLeadFields = { ...defaultFields, nome };
    Object.entries(map).forEach(([header, key]) => {
      if (!key || key === 'nome') return;
      const val = (raw[header] ?? '').trim();
      if (val) (fields as unknown as Record<string, string>)[key] = val;
    });

    const rowTags = (raw.tags ?? '')
      .split(';')
      .map(t => t.trim())
      .filter(Boolean);
    const tagNames = [...new Set([...defaultTags, ...rowTags])];

    rows.push({ rowNumber, label: nome, fields, tagNames });
  });

  return { profile, rows, errors, mappedColumns, ignoredColumns, hasRequiredColumn };
};
